import { useEffect, useMemo, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { BufferAttribute, BufferGeometry, DynamicDrawUsage } from 'three';
import type { LineBasicMaterial, Points, PointsMaterial, LineSegments } from 'three';
import type { RefObject } from 'react';
import { sampleDrivingTerrain } from '../model/drivingTerrain';
import type { VehicleState } from '../model/vehicleDriving';
import type { RuntimeWeather } from '../model/weatherTransition';

/** 최대 용량. 실제 사용량은 품질 설정과 강수 세기로 draw range를 줄입니다. */
const MAX_PARTICLES = 420;
const VOLUME_RADIUS = 24;
const VOLUME_HEIGHT = 25;
const FRAME_STEP_LIMIT = 1 / 20;

interface PrecipitationPool {
  rain: BufferGeometry;
  snow: BufferGeometry;
  x: Float32Array;
  y: Float32Array;
  z: Float32Array;
  phase: Float32Array;
  initialized: boolean;
}

function dynamicAttribute(array: Float32Array, itemSize: number): BufferAttribute {
  const attribute = new BufferAttribute(array, itemSize);
  attribute.setUsage(DynamicDrawUsage);
  return attribute;
}

function createPrecipitationPool(): PrecipitationPool {
  const snow = new BufferGeometry();
  snow.setAttribute('position', dynamicAttribute(new Float32Array(MAX_PARTICLES * 3), 3));
  const rain = new BufferGeometry();
  rain.setAttribute('position', dynamicAttribute(new Float32Array(MAX_PARTICLES * 6), 3));
  return {
    rain,
    snow,
    x: new Float32Array(MAX_PARTICLES),
    y: new Float32Array(MAX_PARTICLES),
    z: new Float32Array(MAX_PARTICLES),
    phase: Float32Array.from({ length: MAX_PARTICLES }, () => Math.random() * Math.PI * 2),
    initialized: false,
  };
}

function respawnParticle(
  pool: PrecipitationPool,
  index: number,
  anchorX: number,
  groundY: number,
  anchorZ: number,
  randomHeight = true
): void {
  const angle = Math.random() * Math.PI * 2;
  const radius = Math.sqrt(Math.random()) * VOLUME_RADIUS;
  pool.x[index] = anchorX + Math.cos(angle) * radius;
  pool.z[index] = anchorZ + Math.sin(angle) * radius;
  pool.y[index] = groundY + (randomHeight ? Math.random() : 1) * VOLUME_HEIGHT;
}

export interface WeatherEffectsProps {
  weather: RuntimeWeather;
  driving: boolean;
  drivingStateRef: RefObject<VehicleState>;
  /** 그래픽 품질의 입자 비율(0~1). */
  particleRatio?: number;
}

/**
 * 차량/카메라 주변의 작은 볼륨만 순환시켜 대형 트랙 전체에 입자를 깔지 않습니다.
 *
 * 비와 눈 지오메트리를 모두 유지합니다. 동적 날씨에서 한쪽 세기를 줄이고 다른 쪽을 올려
 * 교차 페이드하려면 두 버퍼가 동시에 살아 있어야 합니다(전환마다 재할당하면 끊깁니다).
 */
export function WeatherEffects({
  weather,
  driving,
  drivingStateRef,
  particleRatio = 1,
}: WeatherEffectsProps) {
  const camera = useThree((state) => state.camera);
  const pool = useMemo(createPrecipitationPool, []);

  const rainRef = useRef<LineSegments | null>(null);
  const snowRef = useRef<Points | null>(null);
  const rainMaterialRef = useRef<LineBasicMaterial | null>(null);
  const snowMaterialRef = useRef<PointsMaterial | null>(null);
  const qualityRatio = Math.min(1, Math.max(0, particleRatio));

  useEffect(() => {
    pool.initialized = false;
  }, [pool, driving]);

  useEffect(
    () => () => {
      pool.rain.dispose();
      pool.snow.dispose();
    },
    [pool]
  );

  useFrame((_, frameDelta) => {
    /*
     * 강수 세기는 렌더가 아니라 여기서 읽습니다. 전환 중에 컴포넌트를 다시 렌더하면
     * 노면·환경까지 함께 재조정되므로, 가시성과 투명도도 객체에 직접 씁니다.
     */
    const intensity = Math.max(weather.rainIntensity, weather.snowIntensity);
    const activeCount = Math.min(
      MAX_PARTICLES,
      Math.round(MAX_PARTICLES * qualityRatio * Math.min(1, intensity))
    );
    const showRain = weather.rainIntensity > 0.01 && activeCount > 0;
    const showSnow = weather.snowIntensity > 0.01 && activeCount > 0;
    if (rainRef.current !== null) {
      rainRef.current.visible = showRain;
    }
    if (snowRef.current !== null) {
      snowRef.current.visible = showSnow;
    }
    if (rainMaterialRef.current !== null) {
      rainMaterialRef.current.opacity = 0.2 + weather.rainIntensity * 0.34;
    }
    if (snowMaterialRef.current !== null) {
      snowMaterialRef.current.opacity = 0.7;
      snowMaterialRef.current.size = 0.12 + weather.snowIntensity * 0.11;
    }
    if (activeCount === 0) {
      pool.snow.setDrawRange(0, 0);
      pool.rain.setDrawRange(0, 0);
      return;
    }

    const dt = Math.min(Math.max(frameDelta, 0), FRAME_STEP_LIMIT);
    const drivingState = drivingStateRef.current;
    const anchorX = driving ? drivingState.x : 0;
    const anchorZ = driving ? drivingState.z : 0;
    const groundY = driving
      ? sampleDrivingTerrain(anchorX, anchorZ).height + 0.08
      : Math.min(0, camera.position.y - 1);
    if (!pool.initialized) {
      for (let index = 0; index < MAX_PARTICLES; index += 1) {
        respawnParticle(pool, index, anchorX, groundY, anchorZ, true);
      }
      pool.initialized = true;
    }

    const snowPositions = (pool.snow.getAttribute('position') as BufferAttribute)
      .array as Float32Array;
    const rainPositions = (pool.rain.getAttribute('position') as BufferAttribute)
      .array as Float32Array;
    // 비 비중이 높으면 빠르게, 눈 비중이 높으면 천천히 떨어집니다.
    const rainShare = weather.rainIntensity / Math.max(0.001, intensity);
    const fallSpeed = (3.2 + rainShare * 28) * (0.65 + intensity * 0.55);
    const lineLength = 0.8 + weather.rainIntensity * 1.35;
    const elapsed = performance.now() * 0.001;

    for (let index = 0; index < activeCount; index += 1) {
      pool.y[index] -= fallSpeed * dt;
      if (rainShare > 0.5) {
        pool.x[index] += 1.6 * dt;
        pool.z[index] += 0.55 * dt;
      } else {
        pool.x[index] += Math.sin(elapsed + pool.phase[index]) * 0.42 * dt;
        pool.z[index] += Math.cos(elapsed * 0.72 + pool.phase[index]) * 0.34 * dt;
      }

      const offsetX = pool.x[index] - anchorX;
      const offsetZ = pool.z[index] - anchorZ;
      if (
        pool.y[index] <= groundY ||
        Math.abs(offsetX) > VOLUME_RADIUS ||
        Math.abs(offsetZ) > VOLUME_RADIUS
      ) {
        respawnParticle(pool, index, anchorX, groundY, anchorZ, false);
      }

      const pointOffset = index * 3;
      snowPositions[pointOffset] = pool.x[index];
      snowPositions[pointOffset + 1] = pool.y[index];
      snowPositions[pointOffset + 2] = pool.z[index];

      const lineOffset = index * 6;
      rainPositions[lineOffset] = pool.x[index];
      rainPositions[lineOffset + 1] = pool.y[index];
      rainPositions[lineOffset + 2] = pool.z[index];
      rainPositions[lineOffset + 3] = pool.x[index] - 0.07;
      rainPositions[lineOffset + 4] = pool.y[index] + lineLength;
      rainPositions[lineOffset + 5] = pool.z[index] - 0.03;
    }

    // 실제로 갱신한 구간만 그립니다. 나머지는 GPU로 올릴 이유가 없습니다.
    pool.snow.setDrawRange(0, showSnow ? activeCount : 0);
    pool.rain.setDrawRange(0, showRain ? activeCount * 2 : 0);
    (pool.snow.getAttribute('position') as BufferAttribute).needsUpdate = true;
    (pool.rain.getAttribute('position') as BufferAttribute).needsUpdate = true;
  });

  /*
   * 둘을 항상 마운트해 둡니다. 세기에 따라 조건부로 렌더하면 비→눈 교차 페이드마다
   * 지오메트리가 마운트/언마운트되어 입자가 끊깁니다. 가시성은 프레임 루프가 정합니다.
   */
  return (
    <>
      <lineSegments ref={rainRef} geometry={pool.rain} frustumCulled={false} renderOrder={20} visible={false}>
        <lineBasicMaterial
          ref={rainMaterialRef}
          color="#c6d8e4"
          transparent
          opacity={0}
          depthWrite={false}
        />
      </lineSegments>
      <points ref={snowRef} geometry={pool.snow} frustumCulled={false} renderOrder={20} visible={false}>
        <pointsMaterial
          ref={snowMaterialRef}
          color="#f5f8fa"
          size={0.12}
          transparent
          opacity={0}
          depthWrite={false}
          sizeAttenuation
        />
      </points>
    </>
  );
}
